// TypeScript interfaces for vendor transaction history

// Status enums following DRCR patterns
export enum VendorTransactionType {
  ACCPAY = 'ACCPAY',
  ACCPAYBILL = 'ACCPAYBILL',
  SPEND = 'SPEND',
  SPEND_MONEY = 'SPEND_MONEY'
}

export enum VendorTransactionStatus {
  PAID = 'PAID',
  AUTHORISED = 'AUTHORISED',
  DRAFT = 'DRAFT',
  VOIDED = 'VOIDED',
  DELETED = 'DELETED'
}

export interface VendorHistoryFilters {
  startDate?: string; // ISO date string
  endDate?: string; // ISO date string
  minAmount?: number;
  maxAmount?: number;
  transactionTypes?: VendorTransactionType[];
  statuses?: VendorTransactionStatus[];
  includeLineItems?: boolean;
  limit?: number; // Default 50, max 100
  page?: number; // For pagination
}

export interface VendorTransactionSummary {
  transaction_id: string;
  document_number: string;
  document_reference?: string;
  type: string; // Simplified type for business logic (bill/invoice)
  transaction_type?: string; // Original Xero transaction type (SPEND, SPEND_MONEY, PAYMENT, ACCPAY, etc.) for display
  date_issued: string; // ISO date string
  date_due?: string; // ISO date string
  total: number;
  amount_due?: number;
  amount_paid?: number;
  currency: string;
  status: string;
  source: string; // 'xero', 'qbo', etc.
  source_id: string;
  has_attachments?: boolean;
  has_amortization_schedules?: boolean;
  line_items: VendorLineItemSummary[];
}

export interface VendorLineItemSummary {
  id: string | number;
  description: string;
  unit_amount: number;
  quantity: number;
  amount?: number; // Calculated: unit_amount * quantity
  account_code?: string;
  account_id?: string;
  account_name?: string; // Resolved from chart of accounts
  tax_amount?: number;
  tax_type?: string;
  formatted_tax?: string; // e.g., "GST 10% - $45.00"
  formatted_account?: string; // e.g., "6000 - Office Expenses"
}

export interface VendorHistoryStats {
  total_transactions: number;
  total_amount: number;
  average_transaction_amount: number;
  currency: string; // Most common currency
  earliest_transaction?: string; // ISO date string
  latest_transaction?: string; // ISO date string
  date_range_days?: number; // Number of days between earliest and latest
  transaction_types?: Record<string, number>; // Count by type (optional for basic stats)
  status_breakdown?: Record<string, number>; // Count by status (optional for basic stats) 
  common_line_items?: VendorCommonLineItem[]; // Most frequent line items (optional for basic stats)
  most_common_account?: {
    account_code: string;
    account_name?: string;
    frequency: number;
  };
}

export interface VendorCommonLineItem {
  description: string;
  frequency: number; // Number of times this description appears
  account_code?: string;
  account_name?: string;
  average_amount: number;
  total_amount: number;
  currency: string;
}

export interface VendorHistoryResponse {
  vendor_name: string;
  vendor_id?: string; // contact_id if available
  entity_id: string;
  entity_name: string;
  transactions: VendorTransactionSummary[];
  stats: VendorHistoryStats;
  pagination: {
    current_page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
}

export interface VendorHistoryRequest {
  // Path parameters handled separately: entityId, vendorName
  // This interface only contains query parameters/filters
  filters?: VendorHistoryFilters;
}

// Chart of accounts - using existing DRCR patterns
// Note: Will use existing Account interface from @/lib/api for actual implementation
export interface ChartOfAccountsCache {
  entity_id: string;
  accounts: any[]; // Will use Account[] from @/lib/api in implementation
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

// Cache interface for performance
export interface VendorHistoryCache {
  key: string; // Hash of request parameters
  data: VendorHistoryResponse;
  timestamp: number;
  ttl: number; // Time to live in milliseconds (5 minutes default)
}

// Component display props
export interface VendorHistoryDisplayProps {
  vendorName: string;
  entityId: string;
  vendorId?: string;
  onTransactionClick?: (transaction: VendorTransactionSummary) => void;
  showStats?: boolean;
  showLineItems?: boolean;
  maxTransactions?: number; // For summary displays
  className?: string;
  compact?: boolean; // For popover display vs full page
  hideHeaders?: boolean; // Hide all section headers and titles
}

// Hook return interface
export interface UseVendorHistoryResult {
  data: VendorHistoryResponse | null;
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
  clearCache: () => void;
}

// Utility interfaces for data processing
export interface TransactionProcessingResult {
  processedTransactions: VendorTransactionSummary[];
  stats: VendorHistoryStats;
  accountsResolved: number;
  taxItemsFormatted: number;
}

export interface AccountResolutionResult {
  account_code: string;
  account_name?: string;
  formatted_account: string; // "6000 - Office Expenses" or just "6000"
  resolved: boolean;
}

export interface TaxFormattingResult {
  tax_type?: string;
  tax_amount?: number;
  formatted_tax: string; // "GST 10% - $45.00" or "No tax"
  has_tax: boolean;
}

// Error handling interfaces
export interface VendorHistoryError {
  code: 'VENDOR_NOT_FOUND' | 'ENTITY_ACCESS_DENIED' | 'API_ERROR' | 'CACHE_ERROR';
  message: string;
  details?: {
    entity_id?: string;
    vendor_name?: string;
    api_endpoint?: string;
    cache_key?: string;
  };
  timestamp: number;
}

// Export utility type aliases for cleaner usage
export type VendorHistoryDisplayMode = 'summary' | 'detailed' | 'timeline' | 'stats';
export type VendorHistorySort = 'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc';
export type VendorHistoryStatus = 'loading' | 'success' | 'error' | 'empty';

// Constants for configuration
export const VENDOR_HISTORY_CONFIG = {
  DEFAULT_LIMIT: 50,
  MAX_LIMIT: 100,
  DEFAULT_TTL: 5 * 60 * 1000, // 5 minutes
  CHART_OF_ACCOUNTS_TTL: 15 * 60 * 1000, // 15 minutes
  MAX_CACHE_ENTRIES: 50,
  DEBOUNCE_DELAY: 300, // ms
} as const;

// Type guards for runtime type checking
export function isVendorHistoryResponse(obj: any): obj is VendorHistoryResponse {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.vendor_name === 'string' &&
    typeof obj.entity_id === 'string' &&
    Array.isArray(obj.transactions) &&
    typeof obj.stats === 'object'
  );
}

export function isVendorTransactionSummary(obj: any): obj is VendorTransactionSummary {
  return (
    obj &&
    typeof obj === 'object' &&
    typeof obj.transaction_id === 'string' &&
    typeof obj.total === 'number' &&
    Array.isArray(obj.line_items)
  );
}